import type { Entry, EntrySkeletonType, UnresolvedLink } from 'contentful';

/**
 * Returns an Entry-native module's content `fields`, tolerating a stale flattened
 * payload that predates the Entry-native migration.
 *
 * Entry-native modules (`ENTRY_NATIVE_TYPENAMES`) read content from `props.fields`.
 * The pre-migration adapter instead flattened that same content onto the top level
 * of props, with no `fields` key. The Redis page cache (`getPage`) can outlive a
 * rolling Entry-native deploy and hand a page cached in the old flattened shape to
 * the new renderer; `props.fields` is then `undefined` and a direct
 * `const { ... } = props.fields` destructure throws during SSR, surfacing as an
 * HTTP 500. Falling back to the props object itself lets a stale entry render from
 * its top-level keys and degrade to a fresh cache fill instead of erroring.
 *
 * `NoInfer` pins `TFields` to the nested `fields` slot so the well-typed
 * Entry-native props (whose content is not structurally present at the top level)
 * are still accepted at the call site.
 */
export function getEntryNativeFields<TFields extends object>(props: { fields: TFields } | NoInfer<TFields>): TFields {
  return 'fields' in props ? props.fields : props;
}

export const isResolvedEntry = <Skeleton extends EntrySkeletonType>(
  item: Entry<Skeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> | UnresolvedLink<'Entry'> | undefined,
): item is Entry<Skeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> => item != null && 'fields' in item;
